"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const CreaseAnalyzer_1 = require("../analysis/CreaseAnalyzer");
const common_1 = require("../common");
const defaultSVGStyles = `
    .component { fill: #00cc99; fill-opacity: 0.5; stroke: #00cc99; stroke-width: 7; stroke-linejoin: round; }
    .face { stroke: white; stroke-width: 5; stroke-linejoin: round; }
    .face.U { fill: lightblue; }   /* unspecified */
    .face.T { fill: lightgray; }   /* tab */
    .face.G { fill: lightsalmon; } /* gusset */
    .face-background { fill: white; stroke: none; }
    .face-label { fill: black; font: 6px sans-serif; text-anchor: middle; }
    .edge { stroke: black; stroke-width: 1; stroke-linecap: round; stroke-miterlimit: 10; fill: none; }
    .edge.M { stroke: #ff0000; }   /* mountain crease */
    .edge.V { stroke: #0000ff; }   /* valley crease */
    .edge.U { stroke: #ff00ff; }   /* unspecified crease */
    .edge.C { stroke: #00ff00; }   /* cut */
    .edge.F { stroke: #00ffff; }   /* facet crease */
    .edge-background { fill: #808080; stroke: none; }
    .edge-background.M { fill: #ff8080; }
    .edge-background.V { fill: #8080ff; }
    .edge-background.U { fill: #ff80ff; }
    .edge-background.C { fill: #80ff80; }
    .edge-background.F { fill: #80ffff; }
    .edge-label { fill: white; font: 6px sans-serif; text-anchor: middle; }
    .edge-angle-background { fill: #ffffff; stroke: #000000; stroke-width: 0.5; }
    .edge-angle-background.M { stroke: #ff8080; }
    .edge-angle-background.V { stroke: #8080ff; }
    .edge-angle-background.U { stroke: #ff80ff; }
    .edge-angle-background.C { stroke: #80ff80; }
    .edge-angle-background.F { stroke: #80ffff; }
    .edge-angle-label { fill: #000000; font: 6px sans-serif; text-anchor: middle; }
    .edge-angle-label.M { fill: #ff8080; }
    .edge-angle-label.V { fill: #8080ff; }
    .edge-angle-label.U { fill: #ff80ff; }
    .edge-angle-label.C { fill: #80ff80; }
    .edge-angle-label.F { fill: #80ffff; }
    .dual-edge { stroke: yellow; stroke-width: 3; stroke-linecap: round; fill: none; }
    .vertex { fill: black; stroke: none; }
    .vertex-label { fill: white; font: 6px sans-serif; text-anchor: middle; }
`;
/**
 * A utility class for creating SVG visualizations of Crease objects.
 */
class CreaseToSVG {
    /**
     * Returns the default SVG styles used by `getSVG` and `saveSVG`.
     */
    static get defaultSVGStyles() {
        return defaultSVGStyles;
    }
    /**
     * Returns an SVG visualization of the given Crease object.
     * @param {Crease} crease A Crease object.
     * @param {Object} [options = {}] Options for conversion to SVG.
     * @param {number} [options.scale = 1] Scale to apply to the Crease object.
     * @param {number[]} [options.offset = [0, 0]] Translation to apply to the Crease object.
     * @param {number} [options.margin = 10] Margin to include when calculating the SVG view box.
     * @param {string} [options.svgStyles] CSS styles to include in the SVG file. Defaults to
     * `CreaseToSVG.defaultSVGStyles`.
     * @param {boolean} [options.includeComponents = false] If true, include the connected
     * components.
     * @param {boolean} [options.includeDualGraph = false] If true, include the dual graph,
     * illustrating connections between adjacent faces.
     * @param {boolean} [options.includeFoldAngles = false] If true, include a numeric label
     * for each edge with a defined fold angle.
     * @param {boolean} [options.includeLabels = false] If true, include a label for each vertex,
     * edge, and face.
     * @param {boolean} [options.classifyFaces = false] If true, color faces according to function.
     * @param {boolean} [options.useRandomFaceColors = false] If true, fill each face with a random
     * color.
     * @returns {string} An SVG visualization of the Crease object.
     */
    static getSVG(crease, options = {}) {
        // Extract options.
        const scale = options.scale || 1;
        const offset = options.offset || [0, 0];
        const margin = options.margin || 10;
        const svgStyles = options.svgStyles || CreaseToSVG.defaultSVGStyles;
        // Scale and translate all vertices.
        const creaseCopy = crease.copy();
        creaseCopy.scale(scale);
        creaseCopy.translate(offset);
        // Calculate the view box from the bounds and margin.
        const bounds = creaseCopy.getBounds();
        const x = bounds.min[0] - margin;
        const y = bounds.min[1] - margin;
        const width = bounds.max[0] - bounds.min[0] + 2 * margin;
        const height = bounds.max[1] - bounds.min[1] + 2 * margin;
        // Get SVG contents.
        const contents = CreaseToSVG._getSVGContents(creaseCopy, svgStyles, !!options.includeComponents, !!options.includeDualGraph, !!options.includeFoldAngles, !!options.includeLabels, !!options.classifyFaces, !!options.useRandomFaceColors);
        // Create the root element.
        let svg = '<?xml version="1.0" encoding="UTF-8"?>\n';
        svg += `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="${x} ${y} ${width} ${height}" version="1.1">\n`;
        svg += contents;
        svg += '</svg>';
        return svg;
    }
    /**
     * Saves a visualization of the given Crease object in an SVG file.
     * @param {string} filePath The file path of the SVG file.
     * @param {WritableFileSystem} fs The Node.js file system.
     * @param {Crease} crease A Crease object.
     * @param {Object} [options = {}] Options for conversion to SVG.
     * @param {number} [options.scale = 1] Scale to apply to the Crease object.
     * @param {number[]} [options.offset = [0, 0]] Translation to apply to the Crease object.
     * @param {number} [options.margin = 10] Margin to include when calculating the SVG view box.
     * @param {string} [options.svgStyles] CSS styles to include in the SVG file. Defaults to
     * `CreaseToSVG.defaultSVGStyles`.
     * @param {boolean} [options.includeComponents = false] If true, include the connected
     * components.
     * @param {boolean} [options.includeDualGraph = false] If true, include the dual graph,
     * illustrating connections between adjacent faces.
     * @param {boolean} [options.includeFoldAngles = false] If true, include a numeric label
     * for each edge with a defined fold angle.
     * @param {boolean} [options.includeLabels = false] If true, include a label for each vertex,
     * edge, and face.
     * @param {boolean} [options.classifyFaces = false] If true, color faces according to function.
     * @param {boolean} [options.useRandomFaceColors = false] If true, fill each face with a random
     * color.
     */
    static saveSVG(fs, filePath, crease, options = {}) {
        if (typeof fs === 'string') {
            throw new Error('API change: saveSVG now takes fs as its first parameter.');
        }
        const svg = CreaseToSVG.getSVG(crease, options);
        fs.writeFileSync(filePath, svg);
    }
    static _getSVGContents(crease, svgStyles, includeComponents, includeDualGraph, includeFoldAngles, includeLabels, classifyFaces, useRandomFaceColors) {
        // Classify faces.
        // TODO: Should faces be classified during conversion from SVG or in the Crease library?
        const facesClassification = classifyFaces ? CreaseToSVG._classifyFaces(crease)
            : crease.faces.map(() => 'U');
        // Start SVG content with styles.
        let svg = `  <style>${svgStyles}  </style>\n`;
        let svgLabels = '';
        let svgAngles = '';
        // Create a path for each component.
        if (includeComponents) {
            svg += '  <g id="components">\n';
            crease.components.forEach((component) => {
                svg += `    <path id="component-${component.index}" class="component" d="`;
                component.edgeLoops.forEach((loop) => {
                    loop.edgeRefs.forEach((edgeRef, i) => {
                        const edgePoints = edgeRef.allPoints.map(CreaseToSVG._formatPoint);
                        if (i === 0) {
                            svg += `M${edgePoints[0]}`;
                        }
                        const command = ['L', 'Q', 'C'][edgePoints.length - 2];
                        svg += `${command}${edgePoints.slice(1).join(' ')}`;
                    });
                    svg += 'Z';
                });
                svg += '"/>\n';
            });
            svg += '  </g>\n';
        }
        // Create a path for each face.
        if (facesClassification) {
            const getFaceClass = (face) => (useRandomFaceColors ? '' : facesClassification[face.index]);
            svg += '  <g id="faces">\n';
            svgLabels += '  <g id="face-labels">\n';
            crease.faces.forEach((face) => {
                svg += `    <path id="face-${face.index}" class="face ${getFaceClass(face)}"`;
                if (useRandomFaceColors) {
                    svg += ` fill="${CreaseToSVG._getRandomPastelColor()}"`;
                }
                svg += ' d="';
                face.edgeLoops.forEach((loop) => {
                    loop.edgeRefs.forEach((edgeRef, i) => {
                        const edgePoints = edgeRef.allPoints.map(CreaseToSVG._formatPoint);
                        if (i === 0) {
                            svg += `M${edgePoints[0]}`;
                        }
                        const command = ['L', 'Q', 'C'][edgePoints.length - 2];
                        svg += `${command}${edgePoints.slice(1).join(' ')}`;
                    });
                    svg += 'Z';
                });
                svg += '"/>\n';
                if (includeLabels || includeDualGraph) {
                    const size = 8;
                    const p = CreaseToSVG._getFaceCentroid(face);
                    const x = CreaseToSVG._formatNumber(p[0]);
                    const y = CreaseToSVG._formatNumber(p[1]);
                    svgLabels += `    <circle id="face-${face.index}-background" class="face-background" cx="${x}" cy="${y}" r="${size / 2}"/>\n`;
                    const adjust = face.index >= 100 ? ` textLength="${size}" lengthAdjust="spacingAndGlyphs"` : '';
                    svgLabels += `    <text id="face-${face.index}-label" class="face-label" x="${x}" y="${y}" dy="2"${adjust}>${face.index}</text>\n`;
                }
            });
            svg += '  </g>\n';
            svgLabels += '  </g>\n';
        }
        // Create a path for each edge.
        const layers = [
            { edgeClass: 'U', name: 'creases' },
            { edgeClass: 'V', name: 'valley-creases' },
            { edgeClass: 'M', name: 'mountain-creases' },
            { edgeClass: 'C', name: 'cuts' },
            { edgeClass: 'B', name: 'boundary-edges' },
            { edgeClass: 'F', name: 'facet-creases' },
            { edgeClass: '', name: 'edges' },
        ];
        layers.forEach((layer) => {
            const edges = crease.edges.filter(edge => edge.assignment === layer.edgeClass);
            if (edges.length) {
                svg += `  <g id="${layer.name}">\n`;
                svgLabels += `  <g id="${layer.name.substr(0, layer.name.length - 1)}-labels">\n`;
                const hasFoldAngles = edges.some(edge => edge.foldAngle !== null);
                if (hasFoldAngles) {
                    svgAngles += `  <g id="${layer.name.substr(0, layer.name.length - 1)}-angles">\n`;
                }
                edges.forEach((edge) => {
                    const edgePoints = edge.allPoints.map(CreaseToSVG._formatPoint);
                    const command = ['L', 'Q', 'C'][edgePoints.length - 2];
                    svg += `    <path id="edge-${edge.index}" class="edge ${layer.edgeClass}" d="M${edgePoints[0]}${command}${edgePoints.slice(1).join(' ')}"/>\n`;
                    if (includeFoldAngles && edge.foldAngle !== null) {
                        const width = 16;
                        const height = 8;
                        const radius = 2;
                        const p = common_1.geom.evaluateBezier(edge.allPoints, 1 / 3);
                        const x = CreaseToSVG._formatNumber(p[0] - width / 2 - radius);
                        const y = CreaseToSVG._formatNumber(p[1] - height / 2);
                        svgAngles += `    <rect id="edge-${edge.index}-angle-background" class="edge-angle-background ${layer.edgeClass}" x="${x}" y="${y}" width="${width + 2 * radius}" height="${height}" rx="${radius}" ry="${radius}"/>\n`;
                        const tx = CreaseToSVG._formatNumber(p[0]);
                        const ty = CreaseToSVG._formatNumber(p[1]);
                        const angleText = CreaseToSVG._formatNumber(edge.foldAngle);
                        const adjust = angleText.length > 5 ? ` textLength="${width}" lengthAdjust="spacingAndGlyphs"` : '';
                        svgAngles += `    <text id="edge-${edge.index}-angle" class="edge-angle-label ${layer.edgeClass}" x="${tx}" y="${ty}" dy="2"${adjust}>${angleText}</text>\n`;
                    }
                    if (includeLabels) {
                        const size = 8;
                        const p = common_1.geom.evaluateBezier(edge.allPoints, 2 / 3);
                        const x = CreaseToSVG._formatNumber(p[0] - size / 2);
                        const y = CreaseToSVG._formatNumber(p[1] - size / 2);
                        svgLabels += `    <rect id="edge-${edge.index}-background" class="edge-background ${layer.edgeClass}" x="${x}" y="${y}" width="${size}" height="${size}"/>\n`;
                        const tx = CreaseToSVG._formatNumber(p[0]);
                        const ty = CreaseToSVG._formatNumber(p[1]);
                        const adjust = edge.index >= 100 ? ` textLength="${size}" lengthAdjust="spacingAndGlyphs"` : '';
                        svgLabels += `    <text id="edge-${edge.index}-label" class="edge-label" x="${tx}" y="${ty}" dy="2"${adjust}>${edge.index}</text>\n`;
                    }
                });
                svg += '  </g>\n';
                svgLabels += '  </g>\n';
                if (hasFoldAngles) {
                    svgAngles += '  </g>';
                }
            }
        });
        // Create a path for each edge in the dual graph.
        if (includeDualGraph) {
            const faceCentroids = crease.faces.map(CreaseToSVG._getFaceCentroid);
            svg += '  <g id="dual-graph">\n';
            crease.edges.filter(edge => !edge.isBoundary).forEach((edge) => {
                // Draw a quadratic Bezier from the left face centroid to the right face centroid,
                // passing through the midpoint of the edge between the faces.
                const leftFace = edge.leftFace;
                const rightFace = edge.rightFace;
                const p1 = faceCentroids[leftFace.index];
                const p3 = faceCentroids[rightFace.index];
                const edgeMidpoint = common_1.geom.evaluateBezier(edge.allPoints, 0.5);
                const p2 = CreaseToSVG._getQuadraticControlPoint(p1, p3, edgeMidpoint);
                const q1 = CreaseToSVG._formatPoint(p1);
                const q2 = CreaseToSVG._formatPoint(p2);
                const q3 = CreaseToSVG._formatPoint(p3);
                svg += `    <path id="dual-edge-${edge.index}" class="dual-edge" d="M${q1}Q${q2} ${q3}"/>\n`;
            });
            svg += '  </g>\n';
        }
        // Create a circle and label for each vertex.
        if (includeLabels) {
            svgLabels += '  <g id="vertex-labels">\n';
            crease.vertices.forEach((vertex) => {
                const size = 8;
                const x = CreaseToSVG._formatNumber(vertex.position[0]);
                const y = CreaseToSVG._formatNumber(vertex.position[1]);
                svgLabels += `    <circle id="vertex-${vertex.index}" class="vertex" cx="${x}" cy="${y}" r="${size / 2}"/>\n`;
                const adjust = vertex.index >= 100 ? ` textLength="${size}" lengthAdjust="spacingAndGlyphs"` : '';
                svgLabels += `    <text id="vertex-${vertex.index}-label" class="vertex-label" x="${x}" y="${y}" dy="2"${adjust}>${vertex.index}</text>\n`;
            });
            svgLabels += '  </g>\n';
        }
        // Append the labels to the SVG.
        if (includeLabels || includeDualGraph) {
            svg += svgLabels;
        }
        if (includeFoldAngles) {
            svg += svgAngles;
        }
        return svg;
    }
    /**
     * Returns the control point of a quadratic Bezier curve that starts and ends at the specified
     * points and passes through a given point.
     * @private
     * @param {Vector2} startPoint The start point of the quadratic Bezier curve.
     * @param {Vector2} endPoint The end point of the quadratic Bezier curve.
     * @param {Vector2} interpolatedPoint A point to be interpolated by the curve.
     * @returns {Vector2} The quadratic Bezier's control point.
     */
    static _getQuadraticControlPoint(startPoint, endPoint, interpolatedPoint) {
        const d1 = common_1.vec.distance(interpolatedPoint, startPoint);
        const d2 = common_1.vec.distance(interpolatedPoint, endPoint);
        if (d1 === 0 || d2 === 0) {
            return startPoint;
        }
        const t = d1 / (d1 + d2);
        const s = 1 - t;
        return common_1.vec.divide(common_1.vec.subtract(interpolatedPoint, common_1.vec.add(common_1.vec.multiply(s * s, startPoint), common_1.vec.multiply(t * t, endPoint))), 2 * s * t);
    }
    /**
     * Calculates the centroid (center of mass) of the given face, taking into account holes.
     * @private
     * @param {CreaseFace} face A face.
     * @returns {Vector2} The centroid of the face.
     */
    static _getFaceCentroid(face) {
        const loopsPoints = face.edgeLoops.map(loop => loop.edgeRefs.reduce((points, edgeRef) => points.concat(edgeRef.allPoints.slice(0, -1)), []));
        let pointSum = [0, 0];
        let areaSum = 0;
        loopsPoints.forEach((loopPoints) => {
            const area = common_1.geom.getSignedArea(loopPoints);
            if (Math.abs(area) > 1e-5) {
                const centroid = common_1.geom.getPolygonCentroid(loopPoints);
                pointSum = common_1.vec.add(pointSum, common_1.vec.multiply(area, centroid));
                areaSum += area;
            }
        });
        return common_1.vec.divide(pointSum, areaSum);
    }
    /**
     * Returns a string containing a rounded-off, formatted 2D point.
     * @private
     * @param {Vector2} p A 2D point.
     * @returns {string} A string containing the formatted point.
     */
    static _formatPoint(p) {
        const x = CreaseToSVG._formatNumber(p[0]);
        const y = CreaseToSVG._formatNumber(p[1]);
        return `${x},${y}`;
    }
    /**
     * Returns a string containing a rounded-off, formatted number.
     * @private
     * @param {number} x A number.
     * @returns {string} A string containing the formatted number.
     */
    static _formatNumber(x) {
        return (Math.round(x * 100) / 100).toString(10);
    }
    /**
     * Returns a random pastel color.
     * @private
     * @returns {string} A random pastel color string.
     */
    static _getRandomPastelColor() {
        const min = 12;
        const max = 14;
        const r = CreaseToSVG._getRandomHexDigit(min, max);
        const g = CreaseToSVG._getRandomHexDigit(min, max);
        const b = CreaseToSVG._getRandomHexDigit(min, max);
        return `#${r}${g}${b}`;
    }
    /**
     * Returns a random hex digit between min (inclusive) and max (exclusive).
     * @private
     * @param {number} min The minimum value to use.
     * @param {number} max The maximum value to use.
     * @returns {string} A random hex digit.
     */
    static _getRandomHexDigit(min, max) {
        return (Math.floor(min + (Math.random() * ((max - min) + 1)))).toString(16);
    }
    /**
     * Returns an array of face classifications ('G' for gusset, 'T' for tab, 'U' otherwise).
     * @private
     * @param {Crease} crease A crease object.
     * @returns {string[]} An array of face classifications.
     */
    static _classifyFaces(crease) {
        const analyzer = new CreaseAnalyzer_1.CreaseAnalyzer();
        const facesClassification = analyzer.getFaceClassification(crease);
        return facesClassification;
    }
}
exports.CreaseToSVG = CreaseToSVG;
//# sourceMappingURL=CreaseToSVG.js.map